(() => {
  if (customElements.get('wallify-format-guidance')) return;

  const formatKey = value => {
    const match = String(value || '').replace(/×/g, 'x').match(/(?:^|[^\d])(30|40|50)\s*x\s*(40|50|70)(?=\s|cm|\/|$)/i);
    if (!match) return null;
    const key = `${match[1]}x${match[2]}`;
    return ['30x40', '40x50', '50x70'].includes(key) ? key : null;
  };
  const setText = (node, value) => {
    if (node.textContent !== value) node.textContent = value;
  };
  const toggleClass = (node, name, active) => {
    if (node && node.classList.contains(name) !== active) node.classList.toggle(name, active);
  };

  class WallifyFormatGuidance extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.root = document.getElementById(`MainProduct-${this.dataset.section}`);
      if (!this.root) return;
      this.guidance = {
        '30x40': { label: this.dataset.popular, help: this.dataset.popularHelp },
        '40x50': { label: this.dataset.recommended, help: this.dataset.recommendedHelp },
        '50x70': { label: this.dataset.impact, help: this.dataset.impactHelp }
      };
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.onChange = () => {
        this.schedule();
        clearTimeout(this.afterChange);
        this.afterChange = setTimeout(this.schedule, 160);
      };
      this.onOutside = event => {
        if (this.isOpen && !this.widget?.contains(event.target)) this.close(false);
      };
      this.onResize = () => {
        if (this.isOpen) this.positionMenu();
      };
      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('wallify:premium-change', this.onChange);
      document.addEventListener('pointerdown', this.onOutside);
      window.addEventListener('resize', this.onResize);
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'selected', 'class', 'hidden']
      });
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.root?.removeEventListener('change', this.onChange);
      this.root?.removeEventListener('wallify:premium-change', this.onChange);
      document.removeEventListener('pointerdown', this.onOutside);
      window.removeEventListener('resize', this.onResize);
      if (this.frame) cancelAnimationFrame(this.frame);
      clearTimeout(this.afterChange);
      clearTimeout(this.typeaheadTimer);
      this.frame = null;
      this.destroyControl();
    }

    createControl(select, container) {
      this.select = select;
      this.nativeState = {
        hidden: select.getAttribute('aria-hidden'),
        tabIndex: select.getAttribute('tabindex')
      };
      this.selectWrapper = select.closest('.select') || container;
      this.widget = document.createElement('div');
      this.widget.className = 'wallify-format-select';
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.id = `WallifyFormatButton-${this.dataset.section}`;
      this.button.className = 'wallify-format-select__button';
      this.button.setAttribute('role', 'combobox');
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-label', this.dataset.formatLabel || 'Size');
      this.buttonLabel = document.createElement('span');
      this.buttonLabel.className = 'wallify-format-select__label';
      this.buttonBadge = document.createElement('span');
      this.buttonBadge.className = 'wallify-format-select__badge';
      this.buttonBadge.hidden = true;
      const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      caret.setAttribute('viewBox', '0 0 10 6');
      caret.setAttribute('aria-hidden', 'true');
      caret.classList.add('wallify-format-select__caret');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'm1 1 4 4 4-4');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.4');
      caret.append(path);
      this.button.append(this.buttonLabel, this.buttonBadge, caret);
      this.panel = document.createElement('div');
      this.panel.id = `WallifyFormatList-${this.dataset.section}`;
      this.panel.className = 'wallify-format-select__menu';
      this.panel.setAttribute('role', 'listbox');
      this.panel.setAttribute('aria-label', this.dataset.formatLabel || 'Size');
      this.panel.hidden = true;
      this.button.setAttribute('aria-controls', this.panel.id);
      this.widget.append(this.button, this.panel);
      this.selectWrapper.append(this.widget);
      this.helper = document.createElement('p');
      this.helper.id = `WallifyFormatHelp-${this.dataset.section}`;
      this.helper.className = 'wallify-format-help';
      this.helper.dataset.wallifyFormatHelper = '';
      this.helper.hidden = true;
      // A stale helper can remain when the theme replaces only the native select.
      container.querySelectorAll('[data-wallify-format-helper]').forEach(node => node.remove());
      container.append(this.helper);
      const descriptions = new Set((select.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      descriptions.add(this.helper.id);
      this.button.setAttribute('aria-describedby', Array.from(descriptions).join(' '));
      this.label = container.querySelector('.form__label, label[for]');
      this.onLabelClick = event => {
        event.preventDefault();
        this.button?.focus();
      };
      this.label?.addEventListener('click', this.onLabelClick);
      this.button.addEventListener('click', () => this.isOpen ? this.close(false) : this.open());
      this.button.addEventListener('keydown', event => this.onKeydown(event));
      this.button.addEventListener('blur', () => this.close(false));
      this.panel.addEventListener('mousedown', event => event.preventDefault());
      this.panel.addEventListener('click', event => {
        const row = event.target.closest('[data-wallify-format-index]');
        if (row && this.panel.contains(row)) this.choose(Number(row.dataset.wallifyFormatIndex));
      });
      // The original select retains its form, values, price updates and change handler.
      // Hide it from focus/accessibility only after its replacement has been created.
      select.setAttribute('aria-hidden', 'true');
      select.tabIndex = -1;
      toggleClass(select, 'wallify-format-native', true);
      toggleClass(this.selectWrapper, 'wallify-format-custom-ready', true);
      this.rowsSignature = '';
      this.rows = [];
    }

    destroyControl() {
      if (!this.widget) return;
      this.close(false);
      this.label?.removeEventListener('click', this.onLabelClick);
      if (this.select) {
        for (const [attribute, value] of [['aria-hidden', this.nativeState.hidden], ['tabindex', this.nativeState.tabIndex]]) {
          if (value === null) this.select.removeAttribute(attribute);
          else this.select.setAttribute(attribute, value);
        }
        toggleClass(this.select, 'wallify-format-native', false);
      }
      toggleClass(this.selectWrapper, 'wallify-format-custom-ready', false);
      this.widget.remove();
      this.helper?.remove();
      this.widget = this.button = this.panel = this.select = this.selectWrapper = this.label = this.helper = null;
      this.rows = [];
      this.models = [];
      this.rowsSignature = '';
    }

    update() {
      const picker = this.root.querySelector(`#variant-selects-${CSS.escape(this.dataset.section)}`);
      const containers = picker ? Array.from(picker.children).filter(node => node.classList.contains('product-form__input')) : [];
      const position = containers.findIndex(node => {
        const select = node.querySelector('select');
        return select && Array.from(select.options).some(option => formatKey(option.value));
      });
      if (position < 0) {
        this.destroyControl();
        return;
      }
      const container = containers[position];
      const select = container.querySelector('select');
      let variants = [];
      try {
        variants = JSON.parse(picker.querySelector(':scope > script[type="application/json"]')?.textContent || '[]');
      } catch (_) { /* Missing variant data keeps the original select as fallback. */ }
      if (!Array.isArray(variants) || !variants.length) {
        this.destroyControl();
        return;
      }
      const selectedValues = containers.map(node => node.querySelector('select')?.value ?? node.querySelector('input[type="radio"]:checked')?.value ?? null);
      const isAvailable = option => !option.disabled && !option.classList.contains('disabled') && variants.some(variant => variant.available && variant.options?.[position] === option.value && selectedValues.every((value, index) => index === position || value === null || variant.options?.[index] === value));
      if (this.select !== select || !this.widget?.isConnected) {
        this.destroyControl();
        this.createControl(select, container);
      }
      this.models = Array.from(select.options).map((option, index) => {
        let text = option.textContent.trim();
        const previous = option.dataset.wallifyGuidanceLabel;
        if (previous && text.endsWith(` · ${previous}`)) {
          text = text.slice(0, -(` · ${previous}`).length);
          setText(option, text);
        }
        if (previous) delete option.dataset.wallifyGuidanceLabel;
        const available = isAvailable(option);
        return { index, value: option.value, text, available, hidden: option.hidden || option.style.display === 'none', guide: available ? this.guidance[formatKey(option.value)] : null };
      });
      const signature = JSON.stringify(this.models.map(model => [model.index, model.hidden]));
      if (signature !== this.rowsSignature) {
        const fragment = document.createDocumentFragment();
        this.rows = this.models.map(model => {
          const row = document.createElement('div');
          row.className = 'wallify-format-select__option';
          row.id = `WallifyFormatOption-${this.dataset.section}-${model.index}`;
          row.setAttribute('role', 'option');
          row.dataset.wallifyFormatIndex = model.index;
          row.hidden = model.hidden;
          const label = document.createElement('span');
          label.className = 'wallify-format-select__label';
          const badge = document.createElement('span');
          badge.className = 'wallify-format-select__badge';
          row.append(label, badge);
          fragment.append(row);
          return row;
        });
        this.panel.replaceChildren(fragment);
        this.rowsSignature = signature;
      }
      this.models.forEach(model => {
        const row = this.rows[model.index];
        setText(row.firstElementChild, model.text);
        setText(row.lastElementChild, model.guide?.label || '');
        const hideBadge = !model.guide?.label;
        if (row.lastElementChild.hidden !== hideBadge) row.lastElementChild.hidden = hideBadge;
        row.setAttribute('aria-selected', String(model.index === select.selectedIndex));
        row.setAttribute('aria-disabled', String(!model.available));
      });
      const selected = this.models[select.selectedIndex];
      setText(this.buttonLabel, selected?.text || '');
      setText(this.buttonBadge, selected?.guide?.label || '');
      setText(this.helper, selected?.guide?.help || '');
      const hideBadge = !selected?.guide?.label;
      const hideHelp = !selected?.guide?.help;
      if (this.buttonBadge.hidden !== hideBadge) this.buttonBadge.hidden = hideBadge;
      if (this.helper.hidden !== hideHelp) this.helper.hidden = hideHelp;
      if (this.button.disabled !== select.disabled) this.button.disabled = select.disabled;
      if (select.disabled) this.close(false);
      if (this.isOpen) {
        const indexes = this.enabledIndexes();
        if (!indexes.includes(this.activeIndex)) this.activeIndex = indexes[0];
        if (this.activeIndex === undefined) {
          this.rows.forEach(row => toggleClass(row, 'is-active', false));
          this.button.removeAttribute('aria-activedescendant');
        } else this.setActive(this.activeIndex, false);
      }
    }

    enabledIndexes() {
      return (this.models || []).filter(model => model.available && !model.hidden).map(model => model.index);
    }

    open() {
      const indexes = this.enabledIndexes();
      if (!this.button || this.button.disabled || !this.models.some(model => !model.hidden)) return;
      this.isOpen = true;
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.positionMenu();
      this.setActive(indexes.includes(this.select.selectedIndex) ? this.select.selectedIndex : indexes[0], true);
      this.button.focus({ preventScroll: true });
    }

    close(focus) {
      this.isOpen = false;
      if (this.panel && !this.panel.hidden) this.panel.hidden = true;
      this.button?.setAttribute('aria-expanded', 'false');
      this.button?.removeAttribute('aria-activedescendant');
      if (focus && this.button?.isConnected) this.button.focus({ preventScroll: true });
    }

    positionMenu() {
      const rect = this.button.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const flipped = below < Math.min(320, this.rows.length * 50) && above > below;
      toggleClass(this.widget, 'wallify-format-select--above', flipped);
      this.panel.style.maxHeight = `${Math.max(96, Math.min(320, flipped ? above : below))}px`;
    }

    setActive(index, scroll) {
      if (index === undefined) return;
      this.activeIndex = index;
      this.rows.forEach((row, rowIndex) => toggleClass(row, 'is-active', rowIndex === index));
      const row = this.rows[index];
      this.button.setAttribute('aria-activedescendant', row.id);
      if (scroll) {
        const top = row.offsetTop;
        const bottom = top + row.offsetHeight;
        if (top < this.panel.scrollTop) this.panel.scrollTop = top;
        else if (bottom > this.panel.scrollTop + this.panel.clientHeight) this.panel.scrollTop = bottom - this.panel.clientHeight;
      }
    }

    choose(index) {
      const model = this.models[index];
      if (!model?.available || model.hidden || this.select.disabled) return;
      const native = this.select;
      this.close(true);
      if (native.value === model.value) return;
      native.value = model.value;
      native.dispatchEvent(new Event('input', { bubbles: true }));
      native.dispatchEvent(new Event('change', { bubbles: true }));
      this.update();
    }

    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'Escape') {
        if (this.isOpen) {
          event.preventDefault();
          this.close(true);
        }
        return;
      }
      if (event.key === 'Tab') {
        this.close(false);
        return;
      }
      const indexes = this.enabledIndexes();
      if (!indexes.length) {
        if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
          event.preventDefault();
          if (!this.isOpen) this.open();
          else if (event.key === 'Enter' || event.key === ' ') this.close(true);
        }
        return;
      }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (!this.isOpen) {
          this.open();
          if (event.key === 'Home') this.setActive(indexes[0], true);
          if (event.key === 'End') this.setActive(indexes[indexes.length - 1], true);
          return;
        }
        const current = Math.max(0, indexes.indexOf(this.activeIndex));
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? indexes.length - 1 : (current + (event.key === 'ArrowDown' ? 1 : -1) + indexes.length) % indexes.length;
        this.setActive(indexes[next], true);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (this.isOpen) this.choose(this.activeIndex);
        else this.open();
      } else if (event.key.length === 1 && event.key !== ' ') {
        clearTimeout(this.typeaheadTimer);
        this.typed = (this.typed || '') + event.key.toLocaleLowerCase();
        this.typeaheadTimer = setTimeout(() => { this.typed = ''; }, 650);
        const index = indexes.find(candidate => this.models[candidate].text.toLocaleLowerCase().startsWith(this.typed));
        if (index !== undefined) {
          event.preventDefault();
          if (!this.isOpen) this.open();
          this.setActive(index, true);
        }
      }
    }
  }

  customElements.define('wallify-format-guidance', WallifyFormatGuidance);
})();
